import * as stylex from "@stylexjs/stylex";
import { Plus } from "lucide-react";
import { copy } from "@/copy";
import { type Tag, type TagColor, TagColorSchema } from "@/domain/schemas";
import "@/theme/tokens.stylex";
import { colors, motion, radii } from "@/theme/tokens.stylex";
import "@/theme/type.stylex";
import { type } from "@/theme/type.stylex";
import { Button } from "@/ui/Button";
import { PersonDot } from "@/ui/PersonDot";
import { press } from "@/ui/press";
type NewMemberProps = {
  name: string;
  color: TagColor;
  onColor: (color: TagColor) => void;
  onCreate: () => void;
};
type SuggestionsProps = {
  tags: readonly Tag[];
  onAdd: (id: string) => void;
};
const words = copy.events.who;
const _MAX_NAME = 32;
const styles = {
  preview: {
    kZKoxP: "x1vqgdyp",
    kB7OPa: "x9f619",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kg3NbH: "x1hr3lfm",
    ks0D6T: "x193iq5w",
    kaIpWk: "xqhrb7l",
    kGuDYH: "x1j61zf2",
    k63SB2: "x1cf7cre",
    kVQacm: "xb3r6kr",
    kg5iWk: "xlyipyv",
    khDVqt: "xuxw1ft",
    $$css: true
  },
  swatch: {
    kzqmXN: "x187nhsf",
    kZKoxP: "xn3w4p2",
    kMzoRj: "xc342km",
    kmVPX3: "x1717udv",
    kaIpWk: "xqhrb7l",
    kkrTdU: "x1ypdohk",
    k1ekBW: "x16n73rl",
    kIyJzY: "xlc9y7w",
    kAMwcw: "x1em5f5i",
    $$css: true
  },
  picked: {
    kGVxlE: "x3i0a7z",
    $$css: true
  },
  suggestion: {
    kAzted: "x1wxaq2x",
    kMzoRj: "xc342km",
    kaIpWk: "xqhrb7l",
    kWkggS: "xh540kj",
    kMwMTN: "x1a11ngh",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "x167g77z",
    kZCmMZ: "x1onr9mi",
    kwRFfy: "x1x5flf6",
    kMv6JI: "x17jlq2w",
    kGuDYH: "x1j61zf2",
    k63SB2: "x1cf7cre",
    kkrTdU: "x1ypdohk",
    k1ekBW: "x11xpdln",
    kIyJzY: "x1bpocu2",
    kAMwcw: "x1em5f5i",
    $$css: true
  }
};
const fills = {
  lilac: {
    kWkggS: "x1xnpmgp",
    $$css: true
  },
  sky: {
    kWkggS: "xv6g5m4",
    $$css: true
  },
  coral: {
    kWkggS: "x1aunxf9",
    $$css: true
  },
  mint: {
    kWkggS: "x1mxzhvz",
    $$css: true
  },
  sun: {
    kWkggS: "x11uy0h7",
    $$css: true
  },
  pink: {
    kWkggS: "x1y943kj",
    $$css: true
  },
  lime: {
    kWkggS: "xd0s4ba",
    $$css: true
  },
  rust: {
    kWkggS: "x14u7jty",
    $$css: true
  },
  glacier: {
    kWkggS: "x1sqgj6",
    $$css: true
  },
  amber: {
    kWkggS: "x1jvsd1a",
    $$css: true
  },
  sage: {
    kWkggS: "x1ibsdi1",
    $$css: true
  },
  ice: {
    kWkggS: "x5j0lpm",
    $$css: true
  }
};
export function NewMember({
  name,
  color,
  onColor,
  onCreate
}: NewMemberProps) {
  return <div className="xh540kj x1khhgft x1xn8qrt x106a9eq xv54qhq x78zum5 xdt5ytf x1af02g3">
      <div className="x78zum5 x1a02dak x1qughib x6s0dn4 x1v2ro7d">
        <div className="x78zum5 x1a02dak x6s0dn4 x1v2ro7d xeuugli">
          <span className="xr0iwhn xhunfcg xt3et7w xtvhhri">{words.newMember}</span>
          <span {...stylex.props(styles.preview, fills[color])}>{name}</span>
        </div>
        <Button icon={Plus} onClick={onCreate}>
          {words.addMember}
        </Button>
      </div>
      <fieldset aria-label={words.memberColor} className="xc342km x1ghz6dp x1717udv xeuugli x78zum5 x1a02dak x883omv">
        {TagColorSchema.options.map(option => <button key={option} type="button" aria-label={copy.tags.colors[option]} aria-pressed={option === color} onClick={() => onColor(option)} {...stylex.props(styles.swatch, fills[option], option === color && styles.picked, press.scale)} />)}
      </fieldset>
    </div>;
}
export function Suggestions({
  tags,
  onAdd
}: SuggestionsProps) {
  if (tags.length === 0) return null;
  return <div className="x78zum5 xdt5ytf x167g77z">
      <span aria-hidden="true" className="xr0iwhn xhunfcg xt3et7w xtvhhri">
        {words.suggestions}
      </span>
      <fieldset aria-label={words.suggestions} className="xc342km x1ghz6dp x1717udv xeuugli x78zum5 x1a02dak x167g77z">
        {tags.map(tag => <button key={tag.id} type="button" onClick={() => onAdd(tag.id)} {...stylex.props(styles.suggestion, press.scale)}>
            <PersonDot color={tag.color} />
            {tag.name}
          </button>)}
      </fieldset>
    </div>;
}